// src/components/Sidebar.tsx



import React, { useEffect, useRef, useState } from "react";

import { ProjectAPI, AuthAPI } from "../api/apiClient";

import { useUI } from "../context/UIContext";

import clsx from "clsx";

import { useAuth } from "../context/AuthContext";

import { getConnection } from "../api/signalRClient";

import { useNavigate } from "react-router-dom";



type Project = {

  projectNo: number;

  projectName: string;

  projectDescription?: string;



  // =========================================================

  // COMPANY INFORMATION

  // =========================================================

  companyNo: number;

  companyName: string;

};



type Issue = {

  taskId: number;

  title: string;

  dueDate?: string;

  progressPercent?: number;

  prevProgressPercent?: number;

  isClosed?: boolean;

  taskGroupName?: string;

  hasUnread?: boolean;

};



type CompanyProjectGroup = {

  companyNo: number;

  companyName: string;

  projects: Project[];

};



export default function Sidebar({

  onSelect,

}: {

  onSelect?: () => void;

}) {

  const {

    selectedProjectId,

    setSelectedProjectId,

    selectedProjectName,

    setSelectedProjectName,

    selectedIssueId,

    setSelectedIssueId,

    setViewMode,

    viewMode,

    setSelectedIssueTitle,

    pushUIState,

    setAllIssuesFilters,

  } = useUI();



  const [projects, setProjects] = useState<Project[]>([]);



  const [issuesMap, setIssuesMap] = useState<

    Record<number, Issue[]>

  >({});



  const [expandedProject, setExpandedProject] =

    useState<number | null>(null);



  const [loading, setLoading] =

    useState(false);



  // =========================================================
  // CUSTOM SIDEBAR SCROLLBAR
  // =========================================================

  const sidebarScrollRef =
    useRef<HTMLDivElement | null>(null);

  const [sidebarThumbTop, setSidebarThumbTop] =
    useState(0);

  const [showSidebarScrollbar, setShowSidebarScrollbar] =
    useState(false);

  const SIDEBAR_THUMB_HEIGHT = 38;

  const { token, logout } = useAuth();



  const navigate = useNavigate();









  // =========================================================

  // SUPER ADMIN ACCESS

  // =========================================================



  const [hasSuperAdminAccess, setHasSuperAdminAccess] =

    useState(false);



    const [canAccessNSite, setCanAccessNSite] = useState(false);



  // =========================================================

  // LOAD SUPER ADMIN ACCESS

  // =========================================================



  useEffect(() => {

    if (!token) {

      setHasSuperAdminAccess(false);

      return;

    }



    let mounted = true;



   /*  AuthAPI.me()

      .then((user) => {

        if (!mounted) return;



        const hasSuperAdmin =

          user.companies?.some(

            (company) =>

              company.userType?.toLowerCase() ===

              "superadmin"

          ) ?? false;



        setHasSuperAdminAccess(

          hasSuperAdmin

        );

      })

      .catch((error) => {

        console.error(

          "Failed to load company memberships:",

          error

        );



        if (mounted) {

          setHasSuperAdminAccess(false);

        }

      }); */



      AuthAPI.me()

  .then((user) => {

    if (!mounted) return;



    const companies = user.companies ?? [];



    // Super Admin access

    const hasSuperAdmin =

      companies.some(

        (company) =>

          company.userType?.toLowerCase() ===

          "superadmin"

      );



    setHasSuperAdminAccess(hasSuperAdmin);



    // NSite access

    // Client and External users should NOT see NSite

    const canUseNSite =

      companies.some((company) => {

        const userType =

          company.userType?.trim().toLowerCase();



        return (

          userType !== "client" &&

          userType !== "external"

        );

      });



    setCanAccessNSite(canUseNSite);

  })

  .catch((error) => {

    console.error(

      "Failed to load company memberships:",

      error

    );



    if (mounted) {

      setHasSuperAdminAccess(false);

      setCanAccessNSite(false);

    }

  });



    return () => {

      mounted = false;

    };

  }, [token]);



  // =========================================================

  // LOAD PROJECTS

  // =========================================================



  useEffect(() => {

    if (!token) return;



    let mounted = true;



    setLoading(true);



    ProjectAPI.getAll()

      .then((data) => {

        if (!mounted) return;



        setProjects(

          Array.isArray(data)

            ? data

            : []

        );

      })

      .catch(console.error)

      .finally(() => {

        if (mounted) {

          setLoading(false);

        }

      });



    return () => {

      mounted = false;

    };

  }, [token]);



  // =========================================================

  // GROUP PROJECTS BY COMPANY

  // =========================================================



  const groupedProjects: CompanyProjectGroup[] =

    Object.values(

      projects.reduce(

        (

          groups: Record<

            number,

            CompanyProjectGroup

          >,

          project

        ) => {

          const companyNo =

            project.companyNo;



          if (!groups[companyNo]) {

            groups[companyNo] = {

              companyNo:

                project.companyNo,

              companyName:

                project.companyName ||

                "Unknown Company",

              projects: [],

            };

          }



          groups[

            companyNo

          ].projects.push(project);



          return groups;

        },

        {}

      )

    );



  // =========================================================

  // LOAD ISSUES PER PROJECT WHEN EXPANDED

  // =========================================================



  useEffect(() => {

    if (

      !token ||

      !expandedProject

    ) {

      return;

    }



    let cancelled = false;



    ProjectAPI.getIssues(

      expandedProject

    )

      .then((data) => {

        if (cancelled) return;



        setIssuesMap((m) => ({

          ...m,

          [expandedProject]:

            Array.isArray(data)

              ? data

              : [],

        }));

      })

      .catch(console.error);



    return () => {

      cancelled = true;

    };

  }, [

    expandedProject,

    token,

  ]);



  // =========================================================

  // LOAD ISSUES

  // =========================================================



  const loadIssues = async (

    projectId: number

  ) => {

    const data =

      await ProjectAPI.getIssues(

        projectId

      );



    setIssuesMap((m) => ({

      ...m,

      [projectId]:

        data ?? [],

    }));

  };



  // =========================================================
  // CUSTOM SIDEBAR SCROLLBAR
  // =========================================================

  useEffect(() => {
    const scrollElement = sidebarScrollRef.current;

    if (!scrollElement) return;

    const updateSidebarScrollbar = () => {
      const {
        scrollTop,
        scrollHeight,
        clientHeight,
      } = scrollElement;

      const canScroll =
        scrollHeight > clientHeight;

      setShowSidebarScrollbar(canScroll);

      if (!canScroll) {
        setSidebarThumbTop(0);
        return;
      }

      const maxScrollTop =
        scrollHeight - clientHeight;

      const maxThumbTop =
        clientHeight - SIDEBAR_THUMB_HEIGHT;

      const scrollRatio =
        maxScrollTop > 0
          ? scrollTop / maxScrollTop
          : 0;

      setSidebarThumbTop(
        scrollRatio * maxThumbTop
      );
    };

    updateSidebarScrollbar();

    scrollElement.addEventListener(
      "scroll",
      updateSidebarScrollbar
    );

    const resizeObserver =
      new ResizeObserver(
        updateSidebarScrollbar
      );

    resizeObserver.observe(scrollElement);

    const mutationObserver =
      new MutationObserver(
        updateSidebarScrollbar
      );

    mutationObserver.observe(
      scrollElement,
      {
        childList: true,
        subtree: true,
      }
    );

    return () => {
      scrollElement.removeEventListener(
        "scroll",
        updateSidebarScrollbar
      );

      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  // =========================================================

  // SIGNALR

  // =========================================================



  useEffect(() => {

    if (

      !token ||

      !selectedProjectId

    ) {

      return;

    }



    const conn =

      getConnection();



    if (

      !conn ||

      conn.state !==

        "Connected"

    ) {

      return;

    }



    conn

      .invoke(

        "JoinProject",

        selectedProjectId

      )

      .catch((err) =>

        console.error(

          "JoinProject failed:",

          err

        )

      );



    conn.on(

      "IssueUpdated",

      handleIssueUpdated

    );



    return () =>

      conn.off(

        "IssueUpdated",

        handleIssueUpdated

      );

  }, [

    token,

    selectedProjectId,

  ]);



  // =========================================================

  // PICK PROJECT

  // =========================================================



  function pickProject(

    p: Project

  ) {

    pushUIState();



    setSelectedProjectId(

      p.projectNo

    );



    setSelectedProjectName(

      p.projectName

    );



    setSelectedIssueId(

      null

    );



    setExpandedProject(

      p.projectNo

    );

  }



  // =========================================================

  // PICK ISSUE

  // =========================================================



  async function pickIssue(

    issue: Issue,

    projectId: number

  ) {

    if (!projectId) return;



    pushUIState();



    setSelectedProjectId(

      projectId

    );



    setSelectedIssueId(

      issue.taskId

    );



    setSelectedIssueTitle(

      issue.title

    );



    setViewMode(

      (prev) =>

        prev ===

        "issue-details"

          ? "issue-details"

          : "issue-chat"

    );



    await ProjectAPI.markIssueRead(

      projectId,

      issue.taskId

    );

  }



  // =========================================================

  // HANDLE ISSUE UPDATED

  // =========================================================



  function handleIssueUpdated() {

    if (expandedProject) {

      ProjectAPI.getIssues(

        expandedProject

      )

        .then((data) =>

          setIssuesMap(

            (prev) => ({

              ...prev,

              [expandedProject]:

                data,

            })

          )

        )

        .catch(console.error);

    }

  }



  // =========================================================

  // DUE COLOR

  // =========================================================



  const dueColor = (

    due?: string

  ) => {

    if (!due) {

      return "text-gray-500";

    }



    const d =

      new Date(due);



    const today =

      new Date();



    today.setHours(

      0,

      0,

      0,

      0

    );



    const diff =

      Math.floor(

        (d.getTime() -

          today.getTime()) /

          (1000 *

            60 *

            60 *

            24)

      );



    if (diff < 0) {

      return "text-red-600";

    }



    if (diff === 0) {

      return "text-orange-500";

    }



    return "text-green-600";

  };



  // =========================================================

  // UI

  // =========================================================



  return (

    // <div className="p-3 h-full flex flex-col">

    <div className="px-3 pb-3 h-full flex flex-col">



      {/* =====================================================

          HEADER

      ===================================================== */}



      <div className="p-4 flex items-center justify-between border-b bg-white">



        <div className="flex items-center gap-2">



          <img

            src="/icon.png"

            alt="Baevr Logo"

            className="w-7 h-7 rounded-lg"

          />



          <span className="font-bold text-lg">

            Baevr Live

          </span>



        </div>



        {/* NSite Button */}

        {canAccessNSite && (

        <button

          onClick={() =>

            window.open(

              "http://nsite.baevr.com/home",

              "_blank"

            )

          }

          className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1 rounded-lg transition"

        >

          NSite

        </button>

        )}

        {/* =================================================

            SUPER ADMIN

        ================================================= */}



        {hasSuperAdminAccess && (

          <button

            onClick={() =>

              navigate(

                "/super-admin"

              )

            }

            className="text-sm text-blue-600 hover:underline"

          >

            Super Admin

          </button>

        )}



        {/* =================================================

            LOGOUT

        ================================================= */}



        <button

          onClick={logout}

          className="text-sm text-red-600 hover:underline"

        >

          Logout

        </button>



      </div>



      {/* =====================================================

          LOADING

      ===================================================== */}



      {loading && (

        <div className="px-3 text-sm text-gray-500">

          Loading projects...

        </div>

      )}



      {/* =====================================================

          PROJECT LIST

      ===================================================== */}



      {/* <div className="p-3 flex-1 flex flex-col overflow-y-auto"> */}

      {/* <div className="sidebar-scroll p-3 flex-1 flex flex-col overflow-y-auto"> */}



    <div className="relative flex-1 min-h-0">

      <div
        ref={sidebarScrollRef}
        className="sidebar-scroll h-full p-3 flex flex-col"
      >

        <div className="space-y-4 flex-1 pr-2">



          {/* =================================================

              COMPANY GROUPS

          ================================================= */}



          {groupedProjects.map(

            (company) => (



              <div

                key={

                  company.companyNo

                }

                className="mb-4"

              >



                {/* =============================================

                    COMPANY NAME

                ============================================= */}



                <div className="px-2 pb-2">



                  <div className="font-semibold text-gray-700 text-sm">

                    {company.companyName}

                    {" -"}

                  </div>



                </div>



                {/* =============================================

                    COMPANY PROJECTS

                ============================================= */}



                <div className="ml-2 space-y-2">



                  {company.projects.map(

                    (p) => {



                      const isSelected =

                        selectedProjectId ===

                        p.projectNo;



                      const issues =

                        issuesMap[

                          p.projectNo

                        ] ?? [];



                      const expanded =

                        expandedProject ===

                        p.projectNo;



                      return (

                        <div

                          key={

                            p.projectNo

                          }

                          className="border rounded-lg bg-blue-50 border-blue-200"

                        >



                          {/* =================================

                              PROJECT HEADER

                          ================================= */}



                          <div

                            className={clsx(

                              "flex items-center justify-between px-3 py-2 cursor-pointer transition-colors",

                              isSelected

                                ? "bg-blue-100 border-blue-300"

                                : "hover:bg-blue-100"

                            )}

                            onClick={() => {

                              pickProject(

                                p

                              );



                              onSelect?.();

                            }}

                          >



                            <div>



                              <div className="font-semibold text-base">

                                {p.projectName}

                              </div>



                              {p.projectDescription && (

                                <div className="text-xs text-gray-500">

                                  {

                                    p.projectDescription

                                  }

                                </div>

                              )}



                            </div>



                            <div className="flex items-center gap-2">



                              <button

                                onClick={(

                                  e

                                ) => {



                                  e.stopPropagation();



                                  setExpandedProject(

                                    (

                                      cur

                                    ) =>

                                      cur ===

                                      p.projectNo

                                        ? null

                                        : p.projectNo

                                  );

                                }}

                                className="text-sm px-2 py-1 border rounded text-gray-600"

                                title="Expand issues"

                              >

                                {expanded

                                  ? "▾"

                                  : "▸"}

                              </button>



                            </div>



                          </div>



                          {/* =================================

                              ISSUES

                          ================================= */}



                          {expanded && (

                            <div className="px-3 py-2 border-t bg-gray-50">



                              {issues.length ===

                              0 ? (



                                <div className="text-sm text-gray-500">

                                  No open issues

                                </div>



                              ) : (



                                <div className="space-y-3">



                                  {issues.map(

                                    (

                                      iss

                                    ) => {



                                      const prev =

                                        iss.prevProgressPercent ??

                                        Math.max(

                                          0,

                                          (iss.progressPercent ??

                                            0) -

                                            5

                                        );



                                      const total =

                                        iss.progressPercent ??

                                        0;



                                      const today =

                                        Math.max(

                                          0,

                                          total -

                                            prev

                                        );



                                      const prevWidth =

                                        Math.max(

                                          0,

                                          Math.min(

                                            100,

                                            prev

                                          )

                                        );



                                      const todayWidth =

                                        Math.max(

                                          0,

                                          Math.min(

                                            100,

                                            today

                                          )

                                        );



                                      return (

                                        <div

                                          key={

                                            iss.taskId

                                          }

                                          className={clsx(

                                            "cursor-pointer px-2 py-2 rounded-md transition",

                                            selectedIssueId ===

                                              iss.taskId

                                              ? "bg-gray-300 border-l-4 border-blue-600"

                                              : "bg-white hover:bg-gray-100"

                                          )}

                                          onClick={() => {



                                            pickIssue(

                                              iss,

                                              p.projectNo

                                            );



                                            onSelect?.();

                                          }}

                                        >



                                          <div className="flex items-start justify-between">



                                            <div>



                                              <div

                                                className={`font-medium ${

                                                  iss.hasUnread

                                                    ? "text-blue-700 font-bold"

                                                    : "text-gray-700"

                                                }`}

                                              >

                                                {

                                                  iss.title

                                                }

                                              </div>



                                              {iss.taskGroupName && (

                                                <div className="text-xs text-gray-500 mt-0.5">

                                                  {

                                                    iss.taskGroupName

                                                  }

                                                </div>

                                              )}



                                              <div className="text-xs">



                                                <span

                                                  className={dueColor(

                                                    iss.dueDate

                                                  )}

                                                >

                                                  {iss.dueDate

                                                    ? new Date(

                                                        iss.dueDate

                                                      ).toLocaleDateString()

                                                    : "No date"}

                                                </span>



                                                <span className="ml-2 text-gray-500">

                                                  •{" "}

                                                  {iss.progressPercent ??

                                                    0}

                                                  % done

                                                </span>



                                              </div>



                                              {/* =================================

                                                  PROGRESS

                                              ================================= */}



                                              <div className="mt-2 h-2 w-full bg-gray-200 rounded overflow-hidden">



                                                <div

                                                  style={{

                                                    width: `${prevWidth}%`,

                                                  }}

                                                  className="h-2"

                                                >



                                                  <div

                                                    style={{

                                                      width:

                                                        "100%",

                                                    }}

                                                    className="h-2 bg-green-800"

                                                  />



                                                </div>



                                                {todayWidth >

                                                  0 && (

                                                  <div

                                                    style={{

                                                      width: `${todayWidth}%`,

                                                      marginLeft: `${prevWidth}%`,

                                                      transform: `translateX(-${prevWidth}% )`,

                                                    }}

                                                    className="relative h-2"

                                                  >



                                                    <div

                                                      style={{

                                                        width:

                                                          "100%",

                                                      }}

                                                      className="h-2 bg-green-300 absolute left-0 top-0"

                                                    />



                                                  </div>

                                                )}



                                              </div>



                                            </div>



                                          </div>



                                        </div>

                                      );

                                    }

                                  )}



                                </div>

                              )}



                              {/* =================================

                                  MORE BUTTON

                              ================================= */}



                              <button

                                onClick={(e) => {



                                  e.stopPropagation();



                                  pushUIState();



                                  setSelectedProjectId(

                                    p.projectNo

                                  );



                                  setSelectedProjectName(

                                    p.projectName

                                  );



                                  setSelectedIssueId(

                                    null

                                  );



                                  setSelectedIssueTitle(

                                    null

                                  );



                                  setAllIssuesFilters({

                                    status: "",

                                    taskGroup: "",

                                    fromDue: "",

                                    toDue: "",

                                  });



                                  setViewMode(

                                    "all-issues"

                                  );



                                  onSelect?.();



                                }}

                                className="mt-3 w-full text-center text-sm text-blue-600 hover:underline"

                              >

                                More...

                              </button>



                            </div>

                          )}



                        </div>

                      );

                    }

                  )}



                </div>



              </div>

            )

          )}



          {/* =====================================================

              OLD SUPER ADMIN DASHBOARD

          ===================================================== */}



          {/*

          {localStorage.getItem("role")?.toLowerCase() === "superadmin" && (

            <div

              onClick={() => {

                pushUIState();

                setSelectedProjectId(null);

                setSelectedIssueId(null);

                setViewMode("superadmin");

                onSelect?.();

              }}

              className="bg-white border rounded-lg p-3 mt-3 shadow cursor-pointer hover:bg-gray-50"

            >

              <div className="font-semibold text-blue-600">

                Super Admin Dashboard

              </div>



              <div className="text-sm text-gray-500">

                Manage users & projects

              </div>

            </div>

          )}

          */}



        </div>



      </div>

      {showSidebarScrollbar && (
        <div className="sidebar-custom-scrollbar">
          <div
            className="sidebar-custom-thumb"
            style={{
              top: `${sidebarThumbTop}px`,
            }}
          />
        </div>
      )}

    </div>



      {/* =====================================================

          OLD FOOTER

      ===================================================== */}



      {/*

      <div className="mt-4 p-3 border-t">



        <button

          onClick={() => {

            alert(

              "Create new issue - feature coming"

            );

          }}

          className="w-full py-2 bg-blue-600 text-white rounded"

        >

          + New Issue

        </button>



        <div className="mt-3 flex flex-col gap-2 text-sm">



          <button className="text-left text-gray-600 hover:underline">

            Reports

          </button>



          <button className="text-left text-gray-600 hover:underline">

            Cross-project dashboard

          </button>



        </div>



      </div>

      */}



    </div>

  );

}
